/**
 * Leads Realtime Service
 *
 * Servizio singleton per gestire la connessione realtime Supabase
 * agli aggiornamenti dei leads. Fornisce:
 * - Sottoscrizione efficiente con retry automatico
 * - Aggiornamento automatico dello store
 * - Monitoring e metriche
 */

import { supabase } from '../supabase';
import { useClientStore } from '../stores/clientStore';
import type { Client } from '../types';
import { usePermissionsStore } from '../stores/permissionsStore';
import { isLeadVisible } from '../utils/leadVisibility';

// Tipi di eventi emessi dal service
export type LeadsRealtimeEvent =
  | 'leads-realtime-status';

export interface RealtimeStatusEvent {
  status: 'connected' | 'reconnecting' | 'error' | 'disconnected';
  error?: string;
  attempt?: number;
}

// Cache per reference data
interface ReferenceDataCache {
  users: Map<string, { id: string; email: string; full_name: string }>;
  pipelines: Map<string, { id: string; name: string; color: string }>;
  pipelineStages: Map<string, { id: string; name: string; color: string; sort_order: number }>;
  lastRefresh: Date | null;
}

class LeadsRealtimeService {
  private channel: any = null;
  private isInitialized = false;
  private retryCount = 0;
  private maxRetries = 5;
  private retryTimeout: NodeJS.Timeout | null = null;
  private reconnecting = false;
  private organizationId: string | null = null;

  // Cache per dati di reference
  private cache: ReferenceDataCache = {
    users: new Map(),
    pipelines: new Map(),
    pipelineStages: new Map(),
    lastRefresh: null,
  };

  // Metriche
  private metrics = {
    updatesReceived: 0,
    errors: 0,
    lastUpdateAt: null as Date | null,
    connectedAt: null as Date | null,
  };

  // Pause/Resume mechanism
  private paused = false;
  private bufferedEvents: any[] = [];

  /**
   * Inizializza il servizio e sottoscrive agli aggiornamenti dei leads
   */
  async init(organizationId: string) {
    // [LeadsRealtimeService] init() called with org:', organizationId);

    // Se già inizializzato con la stessa org, skip
    if (this.isInitialized && this.organizationId === organizationId) {
      // [LeadsRealtimeService] Already initialized with same org, skipping');
      return;
    }

    // Se organizationId è cambiato, disconnetti e riconnetti
    if (this.isInitialized && this.organizationId !== organizationId) {
      // [LeadsRealtimeService] Org changed, disconnecting old connection');
      await this.disconnect();
    }

    // Verifica autenticazione
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      // [LeadsRealtimeService] No session found, skipping initialization');
      return;
    }

    // [LeadsRealtimeService] Session valid, proceeding with connection');
    this.organizationId = organizationId;
    this.isInitialized = true;

    // Popola la cache prima di connettersi
    await this.refreshCache();

    await this.connect();
  }

  /**
   * Carica i dati di reference nella cache
   */
  private async refreshCache() {
    if (!this.organizationId) return;

    // [LeadsRealtimeService] 🔄 Refreshing reference data cache...');

    try {
      // Fetch users
      const { data: users } = await supabase
        .from('user_profiles')
        .select('id, email, full_name')
        .eq('organization_id', this.organizationId)
        .eq('active', true);

      if (users) {
        this.cache.users.clear();
        users.forEach(user => {
          this.cache.users.set(user.id, user);
        });
        // [LeadsRealtimeService] ✅ Cached ${users.length} users`);
      }

      // Fetch pipelines
      const { data: pipelines } = await supabase
        .from('lead_pipelines')
        .select('id, name, color')
        .eq('organization_id', this.organizationId);

      if (pipelines) {
        this.cache.pipelines.clear();
        pipelines.forEach(pipeline => {
          this.cache.pipelines.set(pipeline.id, pipeline);
        });
        // [LeadsRealtimeService] ✅ Cached ${pipelines.length} pipelines`);
      }

      // Fetch pipeline stages
      const { data: stages } = await supabase
        .from('lead_pipeline_stages')
        .select('id, name, color, sort_order, pipeline_id')
        .eq('organization_id', this.organizationId);

      if (stages) {
        this.cache.pipelineStages.clear();
        stages.forEach(stage => {
          this.cache.pipelineStages.set(stage.id, stage);
        });
        // [LeadsRealtimeService] ✅ Cached ${stages.length} pipeline stages`);
      }

      this.cache.lastRefresh = new Date();
      // [LeadsRealtimeService] ✅ Cache refresh completed');

    } catch (error) {
      console.error('[LeadsRealtimeService] ❌ Error refreshing cache:', error);
    }
  }

  /**
   * Crea e configura il canale Supabase
   */
  private async connect() {
    try {
      // Cleanup canale precedente se esiste
      if (this.channel) {
        await supabase.removeChannel(this.channel);
        this.channel = null;
      }

      if (!this.organizationId) {
        console.warn('[LeadsRealtimeService] ⚠️ No organizationId, skipping connection');
        return;
      }

      // Crea nuovo canale con ID univoco
      const channelId = `leads_realtime_${Date.now()}`;
      this.channel = supabase.channel(channelId);

      if (!this.channel) {
        throw new Error('Impossibile creare canale Supabase');
      }

      // [LeadsRealtimeService] 🔌 Connecting to leads updates for org:', this.organizationId);

      // Configura sottoscrizione agli UPDATE e INSERT della tabella clients
      this.channel
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'clients',
            filter: `organization_id=eq.${this.organizationId}`
          },
          (payload: any) => this.handleUpdate(payload)
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'clients',
            filter: `organization_id=eq.${this.organizationId}`
          },
          (payload: any) => this.handleInsert(payload)
        )
        .subscribe((status: string, err?: Error) => {
          this.handleSubscriptionStatus(status, err);
        });

    } catch (error) {
      console.error('[LeadsRealtimeService] ❌ Errore connessione:', error);
      this.handleConnectionError(error);
    }
  }

  /**
   * Handler per gli aggiornamenti ricevuti
   */
  private handleUpdate(payload: any) {
    // [LeadsRealtimeService] 📥 Lead updated via Realtime:', payload);

    // Se in pausa, bufferizza l'evento
    if (this.paused) {
      // [LeadsRealtimeService] ⏸️ Updates paused, buffering event');
      this.bufferedEvents.push(payload);
      return;
    }

    const updatedLead = payload.new as Client;

    // Restrizioni di visibilità del ruolo: aggiornamenti di lead fuori visibilità ignorati
    if (!isLeadVisible(updatedLead, usePermissionsStore.getState().getLeadVisibility())) {
      return;
    }

    // Fase 2: ignora le persone-referente (individual senza stato) — non sono lead.
    if (updatedLead.entity_type === 'individual' && !updatedLead.stato_cliente) {
      return;
    }

    // Aggiorna metriche
    this.metrics.updatesReceived++;
    this.metrics.lastUpdateAt = new Date();

    // Arricchisci il payload con i dati dalla cache (no query needed!)
    const enrichedLead = {
      ...updatedLead,
      // Aggiungi assigned_user dalla cache se assigned_to è presente
      assigned_user: updatedLead.assigned_to
        ? this.cache.users.get(updatedLead.assigned_to) || undefined
        : undefined,
      // Aggiungi pipeline dalla cache se pipeline_id è presente
      pipeline: updatedLead.pipeline_id
        ? this.cache.pipelines.get(updatedLead.pipeline_id) || undefined
        : undefined,
      // Aggiungi pipeline_stage dalla cache se pipeline_stage_id è presente
      pipeline_stage: updatedLead.pipeline_stage_id
        ? this.cache.pipelineStages.get(updatedLead.pipeline_stage_id) || undefined
        : undefined,
    };

    // Update the store with the enriched lead data
    useClientStore.getState().updateSingleLead(enrichedLead);
    // [LeadsRealtimeService] ✅ Lead store updated with cached data:', enrichedLead.id);
  }

  /**
   * Handler per i nuovi leads (INSERT)
   */
  private async handleInsert(payload: any) {
    // [LeadsRealtimeService] 📥 New lead inserted via Realtime:', payload);

    // Se in pausa, bufferizza l'evento
    if (this.paused) {
      // [LeadsRealtimeService] ⏸️ Updates paused, buffering INSERT event');
      this.bufferedEvents.push({ ...payload, _eventType: 'INSERT' });
      return;
    }

    const newLead = payload.new as Client;

    // Restrizioni di visibilità del ruolo: il lead fuori pipeline/fonti non entra nel kanban
    if (!isLeadVisible(newLead, usePermissionsStore.getState().getLeadVisibility())) {
      return;
    }

    // Fase 2: ignora le persone-referente (individual senza stato) — non sono lead.
    if (newLead.entity_type === 'individual' && !newLead.stato_cliente) {
      return;
    }

    // Aggiorna metriche
    this.metrics.updatesReceived++;
    this.metrics.lastUpdateAt = new Date();

    // Delay per permettere all'API di completare gli insert correlati
    // (interests, tags, contacts vengono inseriti DOPO il lead)
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Query per fetch del lead completo
    const fetchFullLead = async () => {
      return await supabase
        .from('clients')
        .select(`
          *,
          assigned_user:user_profiles!clients_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          sales_rep:user_profiles!clients_sales_rep_id_fkey (
            id,
            email,
            full_name
          ),
          creator:user_profiles!clients_created_by_fkey (
            id,
            email,
            full_name
          ),
          referrer:referrers!clients_referrer_id_fkey (
            id,
            full_name,
            email,
            commission_rate
          ),
          partner:external_partners!clients_partner_id_fkey (
            id,
            name,
            company,
            email,
            partner_type
          ),
          pipeline:lead_pipelines!pipeline_id (
            id,
            name,
            color
          ),
          pipeline_stage:lead_pipeline_stages!pipeline_stage_id (
            id,
            name,
            color,
            sort_order
          ),
          deals:deals (
            id,
            title,
            status,
            amount
          ),
          projects (
            id,
            name,
            status
          ),
          contacts:client_contacts!client_contacts_client_id_fkey (
            id,
            name,
            phone,
            email,
            role,
            area,
            is_primary,
            is_active,
            notes,
            birth_date,
            newsletter_subscribed,
            newsletter_consent_date
          ),
          interests:client_interests (
            id,
            interest_category_id,
            interest_category:interest_categories (
              id,
              name,
              color
            )
          )
        `)
        .eq('id', newLead.id)
        .single();
    };

    // Primo tentativo
    let { data: fullLead, error } = await fetchFullLead();
    console.log('[LeadsRealtimeService] 📥 Full lead fetched (attempt 1):', fullLead?.id, 'interests:', fullLead?.interests?.length || 0);

    // Se non ha interests, riprova dopo 1 secondo (l'API potrebbe non aver ancora completato)
    if (fullLead && (!fullLead.interests || fullLead.interests.length === 0)) {
      console.log('[LeadsRealtimeService] ⏳ No interests found, retrying in 1s...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      const retry = await fetchFullLead();
      if (retry.data) {
        fullLead = retry.data;
        error = retry.error;
        console.log('[LeadsRealtimeService] 📥 Full lead fetched (attempt 2):', fullLead?.id, 'interests:', fullLead?.interests?.length || 0);
      }
    }

    if (error || !fullLead) {
      console.error('[LeadsRealtimeService] ❌ Error fetching full lead:', error);
      // Fallback: usa i dati base con cache
      const enrichedLead = {
        ...newLead,
        assigned_user: newLead.assigned_to
          ? this.cache.users.get(newLead.assigned_to) || undefined
          : undefined,
        pipeline: newLead.pipeline_id
          ? this.cache.pipelines.get(newLead.pipeline_id) || undefined
          : undefined,
        pipeline_stage: newLead.pipeline_stage_id
          ? this.cache.pipelineStages.get(newLead.pipeline_stage_id) || undefined
          : undefined,
      };
      useClientStore.getState().addSingleLead(enrichedLead);
      return;
    }

    // Aggiungi il lead completo allo store
    useClientStore.getState().addSingleLead(fullLead as Client);
    // [LeadsRealtimeService] ✅ New lead added to store with full data:', fullLead.id);

    // Mostra toast solo all'assegnatario (e non al creatore)
    this.showNewLeadToast(fullLead as Client);
  }

  /**
   * Mostra un toast di notifica per un nuovo lead (solo all'assegnatario)
   */
  private async showNewLeadToast(lead: Client) {
    try {
      // Ottieni l'utente corrente
      const { data: { user } } = await supabase.auth.getUser();
      console.log('[LeadsRealtimeService] 🔔 Toast check - user:', user?.id, 'lead.assigned_to:', lead.assigned_to);

      if (!user) {
        console.log('[LeadsRealtimeService] ❌ No user, skipping toast');
        return;
      }

      // Mostra il toast solo se l'utente corrente è l'assegnatario
      if (lead.assigned_to !== user.id) {
        console.log('[LeadsRealtimeService] ❌ User is not assignee, skipping toast');
        return;
      }

      // Non mostrare il toast se l'utente corrente è il creatore (creazione dal CRM)
      if (lead.created_by === user.id) {
        console.log('[LeadsRealtimeService] ❌ User is the creator, skipping toast');
        return;
      }

      // Costruisci il nome del lead
      const leadName = lead.business_name ||
        [lead.first_name, lead.last_name].filter(Boolean).join(' ') ||
        'Nuovo lead';

      console.log('[LeadsRealtimeService] ✅ Emitting toast event for:', leadName);

      // Emetti evento custom per il toast (gestito in AppProvider o componente React)
      const event = new CustomEvent('new-lead-toast', {
        detail: {
          leadId: lead.id,
          leadName,
        }
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error('[LeadsRealtimeService] Error showing toast:', error);
    }
  }

  /**
   * Gestisce gli stati della sottoscrizione
   */
  private handleSubscriptionStatus(status: string, err?: Error) {
    switch (status) {
      case 'SUBSCRIBED':
        // [LeadsRealtimeService] ✅ Connected to leads updates');
        this.retryCount = 0;
        this.reconnecting = false;
        this.metrics.connectedAt = new Date();
        this.emitEvent('leads-realtime-status', { status: 'connected' });
        break;

      case 'CHANNEL_ERROR':
        console.error('[LeadsRealtimeService] ❌ Errore canale:', err?.message);
        this.metrics.errors++;
        this.handleConnectionError(err);
        break;

      case 'TIMED_OUT':
        console.error('[LeadsRealtimeService] ⏰ Timeout');
        this.handleConnectionError(new Error('Timeout sottoscrizione'));
        break;

      case 'CLOSED':
        console.warn('[LeadsRealtimeService] 🔌 Connection closed');
        if (this.isInitialized && !this.reconnecting) {
          // Riconnetti automaticamente se chiuso inaspettatamente
          this.scheduleReconnect();
        }
        break;
    }
  }

  /**
   * Gestisce errori di connessione con retry exponential backoff
   */
  private handleConnectionError(error: any) {
    if (this.retryCount >= this.maxRetries) {
      console.error('[LeadsRealtimeService] 💀 Max tentativi raggiunto');
      this.emitEvent('leads-realtime-status', {
        status: 'error',
        error: 'Impossibile connettersi'
      });
      return;
    }

    this.scheduleReconnect();
  }

  /**
   * Pianifica riconnessione con exponential backoff
   */
  private scheduleReconnect() {
    if (this.reconnecting || this.retryTimeout) {
      return; // Già in fase di riconnessione
    }

    this.reconnecting = true;
    this.retryCount++;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    const delay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 16000);

    // Reconnecting...

    this.emitEvent('leads-realtime-status', {
      status: 'reconnecting',
      attempt: this.retryCount
    });

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      this.connect();
    }, delay);
  }

  /**
   * Emette un evento custom
   */
  private emitEvent(eventName: LeadsRealtimeEvent, detail: any) {
    try {
      const event = new CustomEvent(eventName, { detail });
      window.dispatchEvent(event);
    } catch (error) {
      console.error(`[LeadsRealtimeService] Errore emissione evento ${eventName}:`, error);
    }
  }

  /**
   * Disconnette il servizio
   */
  async disconnect() {
    // [LeadsRealtimeService] 🔌 Disconnecting...');

    this.isInitialized = false;
    this.reconnecting = false;
    this.organizationId = null;

    // Cancella retry timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    // Rimuovi canale
    if (this.channel) {
      await supabase.removeChannel(this.channel);
      this.channel = null;
    }

    this.emitEvent('leads-realtime-status', { status: 'disconnected' });
  }

  /**
   * Ottieni metriche del servizio
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Forza riconnessione
   */
  async reconnect() {
    const orgId = this.organizationId;
    await this.disconnect();
    if (orgId) {
      await this.init(orgId);
    }
  }

  /**
   * Pausa gli aggiornamenti realtime
   * Gli eventi vengono bufferizzati e applicati al resume
   */
  pause() {
    // [LeadsRealtimeService] ⏸️ Updates paused');
    this.paused = true;
  }

  /**
   * Riprendi gli aggiornamenti realtime
   * Applica tutti gli eventi bufferizzati durante la pausa
   */
  resume() {
    this.paused = false;

    // Applica eventi bufferizzati in sequenza (non in parallelo)
    if (this.bufferedEvents.length > 0) {
      const events = [...this.bufferedEvents];
      this.bufferedEvents = [];

      // Deduplica: tieni solo l'ultimo evento per ogni client ID
      const deduped = new Map<string, any>();
      for (const payload of events) {
        const key = payload.id || payload.new?.id || Math.random().toString();
        deduped.set(key, payload);
      }

      // Processa in sequenza con async IIFE
      (async () => {
        for (const payload of deduped.values()) {
          try {
            if (payload._eventType === 'INSERT') {
              await this.handleInsert(payload);
            } else {
              await this.handleUpdate(payload);
            }
          } catch (err) {
            console.warn('[LeadsRealtimeService] Error processing buffered event:', err);
          }
        }
      })();
    }
  }

  /**
   * Controlla se gli aggiornamenti sono in pausa
   */
  isPaused(): boolean {
    return this.paused;
  }

  /**
   * Svuota il buffer senza applicare gli eventi
   * Utile dopo un refetch manuale per evitare eventi duplicati
   */
  clearBuffer() {
    // [LeadsRealtimeService] 🗑️ Clearing buffered events:', this.bufferedEvents.length);
    this.bufferedEvents = [];
  }
}

// Export singleton instance
export const leadsRealtimeService = new LeadsRealtimeService();
